import React, { Reducer, useCallback, useEffect, useReducer, useState } from "react";
import { usePost } from "../API";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { initializeRTC } from "./RTCConnection";
import { RealtimeClientEvent, RealtimeServerEvent, SessionUpdateEvent, ConversationItem, SessionCreatedEvent, ConversationItemWithReference } from 'openai/resources/beta/realtime/realtime';
import { safeParse } from "../Util";

type WithAKMeta = {
  meta: { time: number, hidden?: boolean, done?: boolean }
};
export type ChatConversationItem = ConversationItemWithReference & WithAKMeta;
export type ControlSignal = { type: 'clear-chat' };
export type ChatEvent = RealtimeServerEvent | RealtimeClientEvent | ControlSignal;

/**
 * Low-level hook that allows working directly with the events send to/from OpenAI Realtime using WebRTC.
 *
 * Only use this if you want to create an entire new mode of interaction. Otherwise, use `useChatChannel()`.
 */
export function useChatEvents<S>(reducer: Reducer<S, ChatEvent>, initial: S, sessionType?: 'survey' | 'distro') {
  const tokenEndpoint = sessionType === 'distro' ? '/program/admin/ai_chat/create' : '/coach/conversation';
  const createConversation = usePost(tokenEndpoint);
  const [channel, setChannel] = useState<RTCDataChannel | null>(null);
  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);
  const [sender, setSender] = useState<RTCRtpSender | null>(null);
  const [channelState, setChannelState] = useState<{ status: 'closed' | 'open' | 'initializing' } | { status: 'error', reason: string }>({ status: 'closed' });
  const [state, dispatch] = useReducer(reducer, initial);
  const [enabled, setEnabled] = useState(false);
  const [realtimeSession, setRealtimeSession] = useState<SessionCreatedEvent["session"] | null>(null);
  const [outputStream, setOutputStream] = useState<MediaStream | null>(null);

  useAsyncEffect(async (cancel) => {
    if (!enabled) {
      return;
    }

    const session = await createConversation({
      modality: 'text',
      persistConversation: false
    }, undefined, cancel);

    const token = session.token;

    if (!token) {
      return;
    }

    setChannelState({ status: 'initializing' });

    const { dataChannel, pc, sender } = await initializeRTC(token, {
      onReady() {
        setChannel(dataChannel);
        setSender(sender);
        setPeerConnection(pc);
        setChannelState({ status: 'open' });
      },
      onTrack(e) {
        if (e.streams[0]) {
          setOutputStream(e.streams[0]);
        }
      },
      onConnectionError(e) {
        setChannelState({ status: 'error', reason: e });
        setSender(null)
        setPeerConnection(null);
        setChannel(null);
      },
      onClose() {
        setChannelState({ status: 'closed' });
        setSender(null);
        setPeerConnection(null);
        setChannel(null);
      },
      onError() {
        setChannelState({ status: 'error', reason: 'unknown' });
        setSender(null);
        setPeerConnection(null);
        setChannel(null);
      }
    });

    cancel.addEventListener('abort', () => {
      dataChannel.close();
      setChannelState({ status: 'closed' })
    }, { once: true, passive: true });

  }, [enabled]);

  useEffect(() => {

    const messageHandler = (msg: MessageEvent) => {
      const event = safeParse(msg.data) as RealtimeServerEvent | null;
      if (event) {
        switch (event?.type) {
          case "session.created":
          case "session.updated":
            setRealtimeSession(event.session);
            break;
          default:
            dispatch(event);
        }
      }
    };

    if (channel) {
      channel.addEventListener('message', messageHandler);
    }

    return () => channel?.removeEventListener('message', messageHandler);
  }, [channel]);

  const sendEvent = useCallback((message: ChatEvent) => {
    if (!channel || channelState.status !== 'open' || channel.readyState !== 'open') {
      return;
    }

    dispatch(message);
    channel.send(JSON.stringify(message));

  }, [dispatch, channel, channelState]);

  const manageAudioSubscription = useCallback((action: 'subscribe' | 'unsubscribe', fn: (event: RTCTrackEvent) => void) => {
    if (action === 'subscribe') {
      peerConnection?.addEventListener('track', fn);
    } else {
      peerConnection?.removeEventListener('track', fn);
    }
  }, []);

  return { manageAudioSubscription, setEnabled, enabled, sendEvent, dispatch, state, status: channelState, realtimeSession, sender, setSender, outputStream };
}

/**
 * Wraps the Chat Events from WebRTC for a more structured hook.
 * Some methods, such as Enabling and Monitoring the Connection and Audio Subscription 
 * are passed through to the consumer of the hook.
 *
 * Provides methods to Send and Manage Messages.
 */
export function useChatChannel(sessionType: 'survey' | 'distro' = 'survey') {

  const chat = useChatEvents(({ map }, action) => {

    function updateItemMeta(itemId: string | undefined, update: Partial<ChatConversationItem["meta"]>) {
      const itemToUpdate = itemId ? map.get(itemId) : undefined;
      if (itemToUpdate) {
        itemToUpdate.meta = {
          ...itemToUpdate.meta,
          ...update
        };
      }
    }
  
    switch (action.type) {
      /**
       * CUSTOM EVENTS
       */
      case "clear-chat":
        return {
          map: new Map(),
          toolCalls: new Map(),
          list: []
        };
      /**
       * CLIENT EVENTS
       */
      case "conversation.item.create":
        map.set(action.item.id ?? uniqueOrderedId(map), {
          ...action.item,
          role: 'user', // 'system' is reserved for hiding output for now, hence why we don't use `action.role`.
          meta: {
            time: Date.now(),
            hidden: action.item.type !== 'message' || action.item.role === 'system'
          }
        });
        break;
      /**
       * SERVER EVENTS
       */
      case "conversation.item.created":
        // remote has created a new conversation item.
        // this typically comes in the form of an audio input or output 
        // item being added to the transcript
        if (action.item.id && !map.has(action.item.id)) {
          map.set(action.item.id, {
            ...action.item,
            meta: {
              time: Date.now(),
              hidden: action.item.type !== 'message' || action.item.role === 'system'
            }
          });
        }
        break;
      case "response.output_item.added":
        // remote added a new response
        if (action.item.id && !map.has(action.item.id)) {
          map.set(action.item.id, {
            ...action.item,
            meta: {
              time: Date.now(),
              hidden: !!action.item.call_id
            }
          });
        }
        break;
      case "conversation.item.input_audio_transcription.delta":
      case "response.audio_transcript.delta":      
      case "response.text.delta": 
        // appending new text onto an item
        const itemToUpdateWithDelta = action.item_id ? map.get(action.item_id) : undefined;
        if (itemToUpdateWithDelta && action.delta) {
          const content = itemToUpdateWithDelta.content = itemToUpdateWithDelta.content ?? [];
          const index = typeof action.content_index === "number" ? action.content_index : 0;
          content[index] = content[index] ?? {};
          content[index].text = [content[index].text ?? "", action.delta.replace(/^\{|\}$/g, "")].join("");
        }
        break;
      case "response.output_item.done":
        // remote has completed the response
        updateItemMeta(action.item?.id, { done: true });
        break;
      case "conversation.item.input_audio_transcription.completed":
        // remote has completed the response
        updateItemMeta(action.item_id, { done: true });
        break;
      case "error":
        console.error(action);
        break;  
      default:
        break;
    }

    return { map, list: Array.from(map.values()) };
  }, { map: new Map(), list: [], toolCalls: new Map() } as {
    map: Map<string, ChatConversationItem>,
    list: (ChatConversationItem)[]
  }, sessionType);

  const updateSession = useCallback((session: SessionUpdateEvent['session']) => {
    chat.sendEvent({
      type: 'session.update',
      session
    });
  }, [chat.sendEvent]);

  const sendChatMessage = useCallback((text: string, opts?: { createResponse?: boolean, allowAudio?: boolean, role?: 'user' | 'system'  }) => {
    const now = Date.now();
    const conversationItem: ConversationItem = {
      id: uniqueOrderedId(chat.state.map, now),
      type: "message",
      role: opts?.role ?? 'user',
      content: [
        {
          type: "input_text",
          text
        }
      ]
    };

    // also send message to remote
    chat.sendEvent({
      type: "conversation.item.create",
      item: conversationItem
    });

    if (opts?.createResponse) {
      chat.sendEvent({
        type: "response.create",
        response: {
          modalities: opts?.allowAudio ? ['text', 'audio'] : ['text']
        }
      });
    }
  }, [chat.sendEvent]);

  const restart = useCallback(() => {
    chat.setEnabled(false);
    chat.sendEvent({ type: 'clear-chat' });
    setTimeout(() => chat.setEnabled(true));
  }, [chat.sendEvent, chat.setEnabled]);

  return {
    updateSession,
    sendChatMessage,
    restart,
    messages: chat.state.list,
    ...chat
  };
}

export const ChatChannelContext = React.createContext({} as ChatChannel);

function uniqueOrderedId(existing: Map<string, any>, timestamp=Date.now()) {
  // generate a (mostly) collision proof ID that creates a natural sort order.
  // - 1743692588848_000
  // - 1743692588848_001
  // - 1743692588849_000
  let existingItemWithTimestamp;
  let dedupeIndex = 0;
  let id: string | undefined;
  do {
    id = `${timestamp}_${dedupeIndex.toString().padStart(3, '0')}`;
    existingItemWithTimestamp = existing.get(`convo_${id}`);
    dedupeIndex += 1;
  } while (existingItemWithTimestamp);

  return id;
}

export type ChatChannel = ReturnType<typeof useChatChannel>;

